import { useCallback } from 'react';
import useTimerCore from './useTimerCore';
import useLapManager from './useLapManager';
import useSegmentTimer from './useSegmentTimer';
import { Lap } from '../types';

/**
 * Options for the useStopwatchTimer hook
 */
interface StopwatchTimerOptions {
  /** Optional callback when timer values update */
  onTimerUpdate?: (timeValues: StopwatchTimerValues) => void;
}

/**
 * Time values returned by the timer update callback
 */
interface StopwatchTimerValues {
  /** Main timer time in milliseconds */
  mainTime: number;
  /** Lap timer time in milliseconds */
  lapTime: number;
  /** Segment timer time in milliseconds */
  segmentTime: number;
}

/**
 * Return type for useStopwatchTimer hook
 */
interface StopwatchTimerResult {
  /** Main timer time in milliseconds */
  mainTime: number;
  /** Lap timer time in milliseconds */
  lapTime: number;
  /** Segment timer time in milliseconds */
  segmentTime: number;
  /** Whether the timer is running */
  isRunning: boolean;
  /** Array of recorded lap data */
  laps: Lap[];
  /** Whether the final lap has been recorded in the workout */
  hasFinalLapBeenRecorded: boolean;
  /** Start all timers (main, lap, and segment) */
  startTimer: () => void;
  /** Pause all timers (main, lap, and segment) */
  pauseTimer: () => void;
  /** Reset all timers (main, lap, and segment) and clear lap data */
  resetTimer: () => void;
  /** Reset only the segment timer while preserving main and lap timers */
  resetSegmentTimer: () => void;
  /**
   * Record a lap and reset segment timer
   * @param callback Optional callback function executed with the recorded lap data
   * @returns The recorded lap data or null if timer is not running
   */
  recordLap: (callback?: (lap: Lap) => void) => Lap | null;
  /**
   * Record the final lap of a workout
   * @param callback Optional callback function executed with the recorded lap data
   * @returns The recorded Lap object or null if no lap was recorded
   */
  recordFinalLap: (callback?: (lap: Lap) => void) => Lap | null;
  /** Reset all timers and immediately start them in one operation */
  resetAndStart: () => void;
}

/**
 * Custom hook for stopwatch timer functionality
 *
 * Handles timing logic for main timer, lap timer, and segment timer
 * using animation frames and absolute timestamps for accuracy
 *
 * @param options - Configuration options for the timer
 * @returns Timer state and control methods
 */
const useStopwatchTimer = ({ onTimerUpdate }: StopwatchTimerOptions = {}): StopwatchTimerResult => {
  // Use the core timer for main time tracking
  const mainTimer = useTimerCore({
    onTimerUpdate: time => {
      if (onTimerUpdate) {
        onTimerUpdate({
          mainTime: time,
          lapTime: lapManager.lapTime,
          segmentTime: segmentTimer.segmentTime,
        });
      }
    },
  });

  // Use the lap manager for lap tracking
  const lapManager = useLapManager({
    mainTime: mainTimer.time,
    isRunning: mainTimer.isRunning,
  });

  // Use the segment timer for segment tracking
  const segmentTimer = useSegmentTimer({
    isRunning: mainTimer.isRunning,
  });

  /**
   * Start all timers
   */
  const startTimer = useCallback(() => {
    mainTimer.startNewTimer();
    lapManager.lapTimer.startNewTimer();
    segmentTimer.segmentTimer.startNewTimer();
  }, [mainTimer, lapManager, segmentTimer]);

  /**
   * Pause all timers
   */
  const pauseTimer = useCallback(() => {
    mainTimer.pauseTimer();
    lapManager.lapTimer.pauseTimer();
    segmentTimer.segmentTimer.pauseTimer();
  }, [mainTimer, lapManager, segmentTimer]);

  /**
   * Reset all timers
   */
  const resetTimer = useCallback(() => {
    mainTimer.resetTimer();
    lapManager.resetLaps();
    segmentTimer.resetSegmentTimer();
  }, [mainTimer, lapManager, segmentTimer]);

  /**
   * Reset and start all timers in one operation
   */
  const resetAndStart = useCallback(() => {
    resetTimer();

    // Use setTimeout to ensure reset is complete
    setTimeout(() => {
      startTimer();
    }, 20);
  }, [resetTimer, startTimer]);

  /**
   * Record a lap and also reset the segment timer
   */
  const recordLap = useCallback(
    (callback?: (lap: Lap) => void) => {
      // First record the lap
      const lapData = lapManager.recordLap(callback);

      // Then reset the segment timer
      if (lapData) {
        segmentTimer.resetSegmentTimer();
      }

      return lapData;
    },
    [lapManager, segmentTimer]
  );

  return {
    // Time states
    mainTime: mainTimer.time,
    lapTime: lapManager.lapTime,
    segmentTime: segmentTimer.segmentTime,
    isRunning: mainTimer.isRunning,
    laps: lapManager.laps,
    hasFinalLapBeenRecorded: lapManager.hasFinalLapBeenRecorded,

    // Timer control methods
    startTimer,
    pauseTimer,
    resetTimer,
    resetSegmentTimer: segmentTimer.resetSegmentTimer,
    recordLap,
    recordFinalLap: lapManager.recordFinalLap,
    resetAndStart,
  };
};

export default useStopwatchTimer;
